import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_API_URL;

interface Team {
  id: number;
  name: string;
  leagueId: number | null;
  leagueName: string;
  memberCount: number;
  members: TeamMember[];
  createdAt: string;
}

interface TeamMember {
  bowlerId: number;
  playerId: number;
  name: string;
  email: string;
  average: number;
  status: 'pending' | 'active' | 'cancelled';
}

interface Bowler {
  id: number;
  name: string;
  email: string;
  average: number;
  playerId: number;
  location: string;
}

interface Notification {
  id: number;
  message: string;
  completed: boolean;
  senderName: string;
  sentFrom: number;
}

interface AdminNotification {
  id: number;
  message: string;
  completed: boolean;
  senderName: string;
  senderEmail: string;
  sentFrom: number;
  isAdminProposal: boolean;
  proposalData?: {
    teamName: string;
    leagueId: number;
    bowlerIds: number[];
    leagueName: string;
    leagueVenue?: string;
    leagueCity?: string;
    leagueCountry?: string;
    startDate?: string;
    endDate?: string;
    teamMembers?: string;
  };
}

const MyTeams: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();

  const [teams, setTeams] = useState<Team[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [adminNotifications, setAdminNotifications] = useState<AdminNotification[]>([]);
  const [availableBowlers, setAvailableBowlers] = useState<Bowler[]>([]);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBowlers, setSelectedBowlers] = useState<number[]>([]);
  const [teamName, setTeamName] = useState('');
  const [creating, setCreating] = useState(false);
  const [activeTab, setActiveTab] = useState<'teams' | 'create' | 'notifications'>('teams');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdTeamInfo, setCreatedTeamInfo] = useState<{
    name: string;
    members: Bowler[];
  } | null>(null);

  useEffect(() => {
    if (user) {
      fetchTeams();
      fetchNotifications();
      fetchAdminNotifications();
    }
  }, [user]);

  const fetchTeams = async () => {
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/player/teams`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTeams(data);
      }
    } catch (error) {
      console.error('Failed to fetch teams:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/player/notifications`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const fetchAdminNotifications = async () => {
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/player/admin-notifications`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Parse JSON message for admin proposals
        const parsedNotifications = data.notifications.map((notif: any) => {
          try {
            const messageData = JSON.parse(notif.message);
            return {
              ...notif,
              proposalData: {
                teamName: messageData.teamName,
                leagueId: messageData.leagueId,
                bowlerIds: messageData.bowlerIds,
                leagueName: messageData.leagueName,
                leagueVenue: messageData.leagueVenue,
                leagueCity: messageData.leagueCity,
                leagueCountry: messageData.leagueCountry,
                startDate: messageData.startDate,
                endDate: messageData.endDate,
                teamMembers: messageData.teamMembers
              }
            };
          } catch {
            // If message is not JSON, return as-is
            return notif;
          }
        });
        setAdminNotifications(parsedNotifications);
      }
    } catch (error) {
      console.error('Failed to fetch admin notifications:', error);
    }
  };

  const fetchAvailableBowlers = useCallback(async () => {
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      params.append('limit', '20');

      const response = await fetch(`${apiUrl}/bowlers/available?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAvailableBowlers(data.bowlers);
      }
    } catch (error) {
      console.error('Failed to fetch bowlers:', error);
    }
  }, [searchTerm]);

  useEffect(() => {
    if (activeTab === 'create') {
      fetchAvailableBowlers();
    }
  }, [activeTab, fetchAvailableBowlers]);

  const handleCreateTeam = async () => {
    if (!teamName.trim() || selectedBowlers.length === 0) {
      // You could also make this a modal in the future
      alert('Please provide a team name and select at least one bowler');
      return;
    }

    setCreating(true);

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/teams`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          teamName: teamName.trim(),
          selectedBowlerIds: selectedBowlers
        })
      });

      if (response.ok) {
        // Get the selected bowlers' info for the modal
        const selectedBowlerInfo = availableBowlers.filter(bowler =>
          selectedBowlers.includes(bowler.id)
        );

        setCreatedTeamInfo({
          name: teamName.trim(),
          members: selectedBowlerInfo
        });

        setTeamName('');
        setSelectedBowlers([]);
        setActiveTab('teams');
        fetchTeams();
        fetchNotifications(); // Refresh notifications for other players
        setShowSuccessModal(true);
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Failed to create team');
      }
    } catch (error) {
      console.error('Failed to create team:', error);
      alert('An error occurred while creating the team');
    } finally {
      setCreating(false);
    }
  };

  const toggleBowlerSelection = (bowlerId: number) => {
    setSelectedBowlers(prev =>
      prev.includes(bowlerId)
        ? prev.filter(id => id !== bowlerId)
        : [...prev, bowlerId]
    );
  };

  const markNotificationComplete = async (notificationId: number) => {
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/notifications/${notificationId}/complete`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        setNotifications(prev =>
          prev.map(notif =>
            notif.id === notificationId
              ? { ...notif, completed: true }
              : notif
          )
        );
      }
    } catch (error) {
      console.error('Failed to mark notification as complete:', error);
    }
  };

  const acceptTeamInvitation = async (notificationId: number, teamId: number) => {
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/team-invitation/accept`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          teamId,
          notificationId
        })
      });

      if (response.ok) {
        // Refresh teams and notifications
        fetchTeams();
        fetchNotifications();
        fetchAdminNotifications();
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Failed to accept invitation');
      }
    } catch (error) {
      console.error('Failed to accept team invitation:', error);
      alert('An error occurred while accepting the invitation');
    }
  };

  const declineTeamInvitation = async (notificationId: number, teamId: number) => {
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/team-invitation/decline`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          teamId,
          notificationId
        })
      });

      if (response.ok) {
        // Refresh teams and notifications
        fetchTeams();
        fetchNotifications();
        fetchAdminNotifications();
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Failed to decline invitation');
      }
    } catch (error) {
      console.error('Failed to decline team invitation:', error);
      alert('An error occurred while declining the invitation');
    }
  };

  const acceptAdminProposal = async (notification: AdminNotification) => {
    if (!notification.proposalData) {
      alert('Invalid proposal data');
      return;
    }

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/admin-team-proposal/accept`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          teamName: notification.proposalData.teamName,
          leagueId: notification.proposalData.leagueId,
          bowlerIds: notification.proposalData.bowlerIds,
          notificationId: notification.id
        })
      });

      if (response.ok) {
        const data = await response.json();
        alert(`🎉 ${t('player.teamCreatedSuccessfully', { teamName: data.teamName })}`);
        // Refresh teams and notifications
        fetchTeams();
        fetchAdminNotifications();
      } else if (response.status === 409) {
        const errorData = await response.json();
        if (errorData.alreadyCreated) {
          alert(`✅ ${t('player.teamCreatedByTeammate')}`);
        } else if (errorData.alreadyInTeam) {
          alert(`⚠️ ${t('player.memberAlreadyInTeam')}`);
        }
        // Refresh to show updated notifications
        fetchAdminNotifications();
      } else {
        const errorData = await response.json();
        alert(errorData.error || t('admin.inviteBowlers.proposalFailed'));
      }
    } catch (error) {
      console.error('Failed to accept admin proposal:', error);
      alert(t('admin.inviteBowlers.proposalFailed'));
    }
  };

  const declineAdminProposal = async (notificationId: number) => {
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/admin-team-proposal/decline`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          notificationId
        })
      });

      if (response.ok) {
        alert(t('player.teamProposalDeclined'));
        fetchAdminNotifications();
      } else {
        const errorData = await response.json();
        alert(errorData.error || t('admin.inviteBowlers.proposalFailed'));
      }
    } catch (error) {
      console.error('Failed to decline admin proposal:', error);
      alert(t('admin.inviteBowlers.proposalFailed'));
    }
  };

  // Helper function to extract team ID from notification message
  const extractTeamIdFromMessage = (message: string): number | null => {
    const match = message.match(/Team ID: (\d+)/);
    return match ? parseInt(match[1]) : null;
  };

  // Helper function to check if notification is a team invitation
  const isTeamInvitation = (message: string): boolean => {
    return message.includes('invited you to join team') && message.includes('Team ID:');
  };

  // Filter teams based on search term
  const filteredTeams = teams.filter(team =>
    team.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!user) {
    navigate('/player/dashboard');
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800">
      {/* Header */}
      <div className="bg-white/10 backdrop-blur-lg border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-white">{t('player.myTeamsTitle')}</h1>
              <p className="text-blue-200 mt-1">{t('player.manageTeamsNotifications')}</p>
            </div>
            <button
              onClick={() => navigate('/player/dashboard')}
              className="px-4 py-2 bg-white/10 border border-white/20 text-white rounded-lg hover:bg-white/20 transition duration-200"
            >
              {t('player.backToDashboard')}
            </button>
          </div>
        </div>
      </div>

      {/* Help Card */}
      
        <div
          className="w-16 h-16 bg-green-600/30 rounded-full flex items-center justify-center shadow-lg cursor-pointer fixed bottom-8 right-8 z-50"
          onClick={() => setShowHelpModal(true)}
        >
          <span className="text-5xl text-green-300 font-bold">?</span>
        </div>
      

      {/* Help Modal */}
      {showHelpModal && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"
          onClick={e => {
            if (e.target === e.currentTarget) setShowHelpModal(false);
          }}
        >
          <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 max-w-lg w-full mx-4 p-8 relative"
           style={{
              maxHeight: '80vh',
              overflowY: 'auto',
              scrollbarWidth: 'thin',
              scrollbarColor: '#6366f1 #18181b'
            }}>
            {/* Cool ? Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-blue-600/30 rounded-full flex items-center justify-center shadow-lg">
                <span className="text-5xl text-blue-300 font-bold">?</span>
              </div>
            </div>
            {/* Instructions Body */}
            <h2 className="text-2xl font-bold text-white text-center mb-4">{t('player.myTeamsHelpTitle')}</h2>
            <div className="text-blue-200 text-base whitespace-pre-line">
              {t('player.myTeamsHelpBody')}
            </div>
            <button
              onClick={() => setShowHelpModal(false)}
              className="mt-8 w-full bg-blue-600 text-white py-2 rounded-lg font-bold hover:bg-blue-700 transition"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      )}


      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Navigation */}
        <div className="mb-8">
          <div className="flex space-x-1 bg-white/10 backdrop-blur-lg rounded-lg p-1">
            {[
              { key: 'teams', label: t('player.myTeamsTitle'), icon: '👥' },
              { key: 'create', label: t('player.createTeam'), icon: '➕' },
              { key: 'notifications', label: t('player.notifications'), icon: '🔔' }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as any)}
                className={`flex-1 flex items-center justify-center space-x-2 py-3 px-4 rounded-md transition duration-200 ${activeTab === tab.key
                  ? 'bg-white/20 text-white'
                  : 'text-blue-200 hover:text-white hover:bg-white/10'
                  }`}
              >
                <span>{tab.icon}</span>
                <span className="font-medium">{tab.label}</span>
                {tab.key === 'notifications' && (() => {
                  const unreadCount = notifications.filter(n => !n.completed).length + adminNotifications.filter(n => !n.completed).length;
                  return unreadCount > 0 && (
                    <span className="bg-red-500 text-white text-xs rounded-full px-2 py-1 ml-2">
                      {unreadCount}
                    </span>
                  );
                })()}
              </button>
            ))}
          </div>
        </div>

        {/* Content based on active tab */}
        {activeTab === 'teams' && (
          <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-white mb-6">{t('player.yourTeams')}</h2>
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder={t('player.searchTeamByName')}
                className="w-full px-4 py-2 mb-6 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-300"
              />

              {loading ? (
                <div className="text-center py-12">
                  <div className="text-4xl mb-4">⏳</div>
                  <p className="text-blue-200 text-lg">{t('player.loadingTeams')}</p>
                </div>
              ) : filteredTeams.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">👥</div>
                  <h3 className="text-xl font-bold text-white mb-2">{t('player.noTeamsYet')}</h3>
                  <p className="text-blue-200 mb-6">{t('player.noTeamsYetMessage')}</p>
                  <button
                    onClick={() => setActiveTab('create')}
                    className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-bold py-3 px-6 rounded-lg transition duration-200"
                  >
                    {t('player.createFirstTeam')}
                  </button>
                </div>
              ) : (
                <div className="max-h-[35em] overflow-y-auto space-y-4 custom-scrollbar"
                  style={{
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'rgba(255,255,255,0.2) transparent'
                  }}
                >
                  {filteredTeams.map((team) => (
                    <div key={team.id} className="bg-white/5 rounded-lg p-4 border border-white/10">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="text-xl font-bold text-white">{team.name}</h3>
                          <p className="text-blue-200 text-sm">
                            {t('league.league')}: {team.leagueName} • {t('player.teamMembers', { count: team.memberCount })}
                          </p>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs ${team.leagueId
                          ? 'bg-green-500/20 text-green-200 border border-green-400'
                          : 'bg-yellow-500/20 text-yellow-200 border border-yellow-400'
                          }`}>
                          {team.leagueId ? t('player.registered') : t('player.notRegistered')}
                        </span>
                      </div>

                      <div className="space-y-2">
                        <h4 className="text-white font-medium">{t('player.teamMembersList')}:</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {team.members.map((member) => (
                            <div key={member.bowlerId} className="bg-white/5 rounded p-2">
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <p className="text-white text-sm font-medium truncate">{member.name}</p>
                                    <span className="text-blue-400 text-xs font-bold whitespace-nowrap">({t('admin.bowlerDetail.avg')}: {member.average})</span>
                                  </div>
                                  <p className="text-blue-300 text-xs truncate">{member.email}</p>
                                </div>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0 ${member.status === 'active'
                                  ? 'bg-green-500/20 text-green-300 border border-green-500/30'
                                  : member.status === 'pending'
                                    ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30'
                                    : 'bg-red-500/20 text-red-300 border border-red-500/30'
                                  }`}>
                                  {member.status.charAt(0).toUpperCase() + member.status.slice(1)}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'create' && (
          <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-white mb-6">{t('player.createNewTeam')}</h2>

              {/* Team Name Input */}
              <div className="mb-6">
                <label className="block text-blue-200 text-sm font-medium mb-2">{t('player.teamName')}</label>
                <input
                  type="text"
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  placeholder={t('player.enterTeamName')}
                  className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Search Bowlers */}
              <div className="mb-4">
                <label className="block text-blue-200 text-sm font-medium mb-2">{t('player.searchBowlers')}</label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={t('player.searchByNameOrEmail')}
                  className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Selected Bowlers */}
              {selectedBowlers.length > 0 && (
                <div className="mb-4">
                  <p className="text-blue-200 text-sm font-medium mb-2">{t('player.selectedCount', { count: selectedBowlers.length })}:</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedBowlers.map((bowlerId) => {
                      const bowler = availableBowlers.find(b => b.id === bowlerId);
                      return bowler ? (
                        <span key={bowlerId} className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm">
                          {bowler.name}
                        </span>
                      ) : null;
                    })}
                  </div>
                </div>
              )}

              {/* Available Bowlers List */}
              <div className="mb-6">
                <p className="text-blue-200 text-sm font-medium mb-2">{t('player.availableBowlers')}:</p>
                <div className="max-h-64 overflow-y-auto bg-white/5 rounded-lg border border-white/10">
                  {availableBowlers.length === 0 ? (
                    <div className="p-4 text-center text-blue-300">
                      {t('player.noBowlersFound')}
                    </div>
                  ) : (
                    availableBowlers.map((bowler) => (
                      <div
                        key={bowler.id}
                        onClick={() => toggleBowlerSelection(bowler.id)}
                        className={`p-3 border-b border-white/10 cursor-pointer transition duration-200 hover:bg-white/10 ${selectedBowlers.includes(bowler.id) ? 'bg-blue-600/30' : ''
                          }`}
                      >
                        <div className="flex justify-between items-center">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-white font-medium truncate">{bowler.name}</p>
                              <span className="text-blue-400 text-sm font-bold whitespace-nowrap">({t('admin.bowlerDetail.avg')}: {bowler.average})</span>
                            </div>
                            <p className="text-blue-400 text-xs truncate">{bowler.location}</p>
                          </div>
                          <div className={`w-5 h-5 rounded-full border-2 ${selectedBowlers.includes(bowler.id)
                            ? 'bg-blue-600 border-blue-600'
                            : 'border-white/40'
                            }`}>
                            {selectedBowlers.includes(bowler.id) && (
                              <div className="w-full h-full flex items-center justify-center">
                                <span className="text-white text-xs">✓</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Create Button */}
              <button
                onClick={handleCreateTeam}
                disabled={creating || !teamName.trim() || selectedBowlers.length === 0}
                className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed text-white font-bold py-3 px-6 rounded-lg transition duration-200"
              >
                {creating ? t('player.creatingTeam') : t('player.createTeam')}
              </button>
            </div>
          </div>
        )}

        {activeTab === 'notifications' && (
          <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-white mb-6">{t('player.notifications')}</h2>

              {notifications.length === 0 && adminNotifications.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">🔔</div>
                  <h3 className="text-xl font-bold text-white mb-2">{t('player.noNotifications')}</h3>
                  <p className="text-blue-200">{t('player.allCaughtUp')}</p>
                </div>
              ) : (
                <div className="max-h-96 overflow-y-auto space-y-3">
                  {/* Admin Team Proposals */}
                  {adminNotifications.map((notification) => (
                    <div
                      key={`admin-${notification.id}`}
                      className={`p-4 rounded-lg border-2 transition duration-200 ${
                        notification.completed
                          ? 'bg-white/5 border-white/10 opacity-60'
                          : 'bg-gradient-to-r from-purple-500/10 to-blue-500/10 border-purple-400/50 hover:from-purple-500/20 hover:to-blue-500/20'
                      }`}
                    >
                      <div className="flex items-start space-x-3">
                        <div className="mt-1">
                          <span className="text-3xl">👥</span>
                        </div>
                        <div className="flex-1">
                          {notification.completed ? (
                            // Completed notification - show simple message
                            <div>
                              <p className="text-white">
                                {notification.proposalData 
                                  ? t('player.teamCreatedSuccessfully', { teamName: notification.proposalData.teamName })
                                  : notification.message}
                              </p>
                            </div>
                          ) : notification.proposalData ? (
                            // Active proposal - show full details
                            <>
                              <div className="flex items-center gap-2 mb-2">
                                <span className="bg-purple-600 text-white text-xs font-bold px-2 py-1 rounded">{t('player.adminProposalBadge')}</span>
                                <span className="text-blue-300 text-sm">{notification.proposalData.leagueName}</span>
                              </div>
                              <h4 className="text-white font-bold text-lg mb-1">
                                {t('player.proposedTeam', { teamName: notification.proposalData.teamName })}
                              </h4>
                              <p className="text-blue-200 text-sm mb-2">
                                {t('player.adminProposedTeam', { senderName: notification.senderName, count: notification.proposalData.bowlerIds.length })}
                              </p>
                              {notification.proposalData.teamMembers && (
                                <div className="bg-white/5 rounded p-2 mb-2">
                                  <p className="text-blue-300 text-xs font-semibold mb-1">👥 {t('player.teamMembersLabel')}</p>
                                  <p className="text-white text-sm">{notification.proposalData.teamMembers}</p>
                                </div>
                              )}
                              {(notification.proposalData.leagueVenue || notification.proposalData.leagueCity) && (
                                <div className="bg-white/5 rounded p-2 mb-2">
                                  <p className="text-blue-300 text-xs font-semibold mb-1">📍 {t('player.locationLabel')}</p>
                                  <p className="text-white text-sm">
                                    {[notification.proposalData.leagueVenue, notification.proposalData.leagueCity, notification.proposalData.leagueCountry]
                                      .filter(Boolean)
                                      .join(', ')}
                                  </p>
                                </div>
                              )}
                              {(notification.proposalData.startDate || notification.proposalData.endDate) && (
                                <div className="bg-white/5 rounded p-2 mb-2">
                                  <p className="text-blue-300 text-xs font-semibold mb-1">📅 {t('player.datesLabel')}</p>
                                  <p className="text-white text-sm">
                                    {notification.proposalData.startDate} - {notification.proposalData.endDate}
                                  </p>
                                </div>
                              )}
                              <p className="text-blue-300 text-xs mb-3">
                                💡 {t('player.oneAcceptCreatesTeam')}
                              </p>
                            </>
                          ) : (
                            // Fallback for notifications without parsed data
                            <p className="text-white">{notification.message}</p>
                          )}
                          <p className="text-blue-400 text-xs">
                            {t('player.fromAdmin', { senderName: notification.senderName, email: notification.senderEmail })}
                          </p>
                        </div>
                        {!notification.completed && notification.proposalData && (
                          <div className="flex flex-col gap-2 ml-4">
                            <button
                              onClick={() => acceptAdminProposal(notification)}
                              className="px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white text-sm font-medium rounded-lg transition duration-200 whitespace-nowrap"
                            >
                              ✅ {t('player.acceptTeamButton')}
                            </button>
                            <button
                              onClick={() => declineAdminProposal(notification.id)}
                              className="px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white text-sm font-medium rounded-lg transition duration-200 whitespace-nowrap"
                            >
                              ❌ {t('player.declineButton')}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Regular Player Notifications */}
                  {notifications.map((notification) => {
                    const isInvitation = isTeamInvitation(notification.message);
                    const teamId = extractTeamIdFromMessage(notification.message);

                    return (
                      <div
                        key={notification.id}
                        className={`p-4 rounded-lg border transition duration-200 ${notification.completed
                          ? 'bg-white/5 border-white/10 opacity-60'
                          : 'bg-blue-500/10 border-blue-400/30 hover:bg-blue-500/20'
                          }`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <p className="text-white">{notification.message.replace(/Team ID: \d+/, '').trim()}</p>
                            <p className="text-blue-300 text-sm mt-1">{t('player.from')}: {notification.senderName}</p>
                          </div>
                          {!notification.completed && (
                            <div className="ml-4 flex gap-2">
                              {isInvitation && teamId ? (
                                <>
                                  <button
                                    onClick={() => acceptTeamInvitation(notification.id, teamId)}
                                    className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-sm rounded transition duration-200"
                                  >
                                    {t('player.accept')}
                                  </button>
                                  <button
                                    onClick={() => declineTeamInvitation(notification.id, teamId)}
                                    className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-sm rounded transition duration-200"
                                  >
                                    {t('player.decline')}
                                  </button>
                                </>
                              ) : (
                                <button
                                  onClick={() => markNotificationComplete(notification.id)}
                                  className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded transition duration-200"
                                >
                                  {t('player.markRead')}
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Success Modal */}
      {showSuccessModal && createdTeamInfo && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowSuccessModal(false);
              setCreatedTeamInfo(null);
            }
          }}
        >
          <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 max-w-md w-full mx-4">
            <div className="p-6">
              {/* Header */}
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">✅</span>
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">{t('player.teamCreatedSuccess')}</h2>
                <p className="text-blue-200">{t('player.teamReadyForRegistration')}</p>
              </div>

              {/* Team Info */}
              <div className="bg-white/5 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-bold text-white mb-3">{t('league.team')}: {createdTeamInfo.name}</h3>
                <div className="space-y-2">
                  <p className="text-blue-200 text-sm font-medium">{t('player.teamMembers', { count: createdTeamInfo.members.length })}:</p>
                  <div className="space-y-2">
                    {createdTeamInfo.members.map((member) => (
                      <div key={member.id} className="bg-white/5 rounded p-2 flex justify-between items-center gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-white text-sm font-medium truncate">{member.name}</p>
                            <span className="text-blue-400 text-xs font-bold whitespace-nowrap">({t('admin.bowlerDetail.avg')}: {member.average})</span>
                          </div>
                        </div>
                        <span className="text-blue-400 text-xs whitespace-nowrap">{member.location}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Notification Info */}
              <div className="bg-blue-500/10 border border-blue-400/30 rounded-lg p-4 mb-6">
                <div className="flex items-start space-x-3">
                  <span className="text-blue-400 text-xl">📧</span>
                  <div>
                    <h4 className="text-white font-medium mb-1">{t('player.notificationsSent')}</h4>
                    <p className="text-blue-200 text-sm">
                      {t('player.notificationsSentMessage')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowSuccessModal(false);
                    setCreatedTeamInfo(null);
                  }}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold py-3 px-4 rounded-lg transition duration-200"
                >
                  {t('player.viewMyTeams')}
                </button>
                <button
                  onClick={() => {
                    setShowSuccessModal(false);
                    setCreatedTeamInfo(null);
                    setActiveTab('create');
                  }}
                  className="bg-white/10 border border-white/20 text-white hover:bg-white/20 font-medium py-3 px-4 rounded-lg transition duration-200"
                >
                  {t('player.createAnother')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyTeams;